import { Injectable, NgZone, Optional } from '@angular/core';
import {
  bufferTime,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { UserIdleConfig } from './user-idle-config';

@Injectable({
  providedIn: 'root',
})
export class UserIdleService {
  ping$!: Observable<unknown>;

  /**
   * Events that can interrupts user's inactivity timer.
   */
  protected activityEvents$!: Observable<unknown>;

  protected timerStart$ = new Subject<boolean>();
  protected idleDetected$ = new Subject<boolean>();
  protected idleRefresh$ = new Subject<void>();
  protected timeout$ = new Subject<boolean>();
  protected idle$!: Observable<unknown>;
  protected timer$!: Observable<number>;
  /**

  /**
   * Idle value in milliseconds.
   * Default equals to 15 minutes.
   */
  protected idleMillisec = 1000 * 60 * 15;
  /**
   * Idle buffer wait time milliseconds to collect user action
   * Default equals to 1 Sec.
   */
  protected idleSensitivityMillisec = 1000;
  /**
   * Timeout value in seconds.
   * Default equals to 5 minutes.
   */
  protected timeoutSec = 60 * 5;
  /**
   * Ping value in milliseconds.
   * Default equals to 2 minutes.
   */
  protected pingMillisec = 1000 * 60 * 2;
  /**
   * Timeout status.
   */
  protected isTimeout = false;

  protected isWatching = false;

  /**
   * Timer of user's inactivity is in progress.
   */
  protected isInactivityTimer = false;
  protected isIdleDetected = false;

  protected idleSubscription!: Subscription;

  constructor(@Optional() config: UserIdleConfig, private _ngZone: NgZone) {
    if (config) {
      this.setConfig(config);
    }
  }

  /**
   * Start watching for user idle and setup timer and ping.
   */
  startWatching() {
    this.isWatching = true;

    if (!this.activityEvents$) {
      this.activityEvents$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'resize'),
        fromEvent(document, 'keydown')
      );
    }

    this.idle$ = from(this.activityEvents$);

    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    // If any of user events is not active for idle-seconds when start timer.
    this.idleSubscription = this.idle$
      .pipe(
        bufferTime(this.idleSensitivityMillisec), // Starting point of detecting of user's inactivity
        filter(
          (arr) =>
            !arr.length &&
            !this.isIdleDetected &&
            !this.isInactivityTimer &&
            this.isWatching
        ),
        tap(() => {
          this.isIdleDetected = true;
          this.idleDetected$.next(true);
        }),
        switchMap(() =>
          this._ngZone.runOutsideAngular(() =>
            interval(1000).pipe(
              takeUntil(
                merge(
                  this.activityEvents$,
                  this.idleRefresh$,
                  timer(this.idleMillisec).pipe(
                    tap(() => {
                      this.isInactivityTimer = true;
                      this.timerStart$.next(true);
                    })
                  )
                )
              ),
              finalize(() => {
                this.isIdleDetected = false;
                this.idleDetected$.next(false);

                if (!this.isInactivityTimer) {
                  localStorage.setItem(
                    'idle-refresh',
                    new Date().toISOString()
                  );
                }
              })
            )
          )
        )
      )
      .subscribe();

    this.setupCrossTabListeners();
    this.setupTimer(this.timeoutSec);
    this.setupPing(this.pingMillisec);
  }

  stopWatching() {
    this.isWatching = false;
    this.stopTimer();
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }
  }

  stopTimer() {
    this.isInactivityTimer = false;
    this.timerStart$.next(false);
  }

  resetTimer() {
    this.stopTimer();
    this.isTimeout = false;
    localStorage.setItem('idle-refresh', new Date().toISOString());
  }

  timeout() {
    this.timeout$.next(true);
  }

  /**
   * Return observable for timer's countdown number that emits after idle.
   */
  onTimerStart(): Observable<number | null> {
    return this.timerStart$.pipe(
      distinctUntilChanged(),
      switchMap((start) => (start ? this.timer$ : of(null)))
    );
  }

  /**
   * Return observable for timer's countdown number that emits after idle.
   */
  onTimerEnd(): Observable<boolean> {
    return this.timerStart$.pipe(filter((timerOn) => !timerOn));
  }

  /**
   * Return observable for idle status changed
   */
  onIdleStatusChanged(): Observable<boolean> {
    return this.idleDetected$.asObservable();
  }

  /**
   * Return observable for when idle cancelled
   */
  onIdleCancelled(): Observable<boolean> {
    return this.idleDetected$.pipe(
      filter((isIdle) => !isIdle && !this.isInactivityTimer)
    );
  }

  /**
   * Return observable for timeout is fired.
   */
  onTimeout(): Observable<boolean> {
    return this.timeout$.pipe(
      filter((timeout) => !!timeout),
      tap(() => (this.isTimeout = true)),
      tap(() => localStorage.setItem('timeout', new Date().toISOString())),
      map(() => true)
    );
  }

  getConfigValue(): UserIdleConfig {
    return {
      idle: this.idleMillisec / 1000,
      idleSensitivity: this.idleSensitivityMillisec / 1000,
      timeout: this.timeoutSec,
      ping: this.pingMillisec / 1000,
    };
  }

  /**
   * Set config values.
   * @param config
   */
  setConfigValues(config: UserIdleConfig) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set config values');
      return;
    }

    this.setConfig(config);
  }

  private setConfig(config: UserIdleConfig) {
    if (config.idle) {
      this.idleMillisec = config.idle * 1000;
    }
    if (config.ping) {
      this.pingMillisec = config.ping * 1000;
    }
    if (config.idleSensitivity) {
      this.idleSensitivityMillisec = config.idleSensitivity * 1000;
    }
    if (config.timeout) {
      this.timeoutSec = config.timeout;
    }
  }

  /**
   * Set custom activity events
   *
   * @param customEvents Example: merge(
   *   fromEvent(window, 'mousemove'),
   *   fromEvent(window, 'resize'),
   *   fromEvent(document, 'keydown'),
   *   fromEvent(document, 'touchstart'),
   *   fromEvent(document, 'touchend')
   * )
   */
  setCustomActivityEvents(customEvents: Observable<unknown>) {
    if (this.idleSubscription && !this.idleSubscription.closed) {
      console.error('Call stopWatching() before set custom activity events');
      return;
    }

    this.activityEvents$ = customEvents;
  }

  /**
   * Setup timer.
   *
   * Counts every seconds and return n+1 and fire timeout for last count.
   * @param timeout Timeout in seconds.
   */
  protected setupTimer(timeout: number) {
    this._ngZone.runOutsideAngular(() => {
      this.timer$ = of(() => new Date()).pipe(
        map((fn) => fn()),
        switchMap((startDate) =>
          interval(1000).pipe(
            takeUntil(this.timerStart$.pipe(filter((timerOn) => !timerOn))),
            map(() =>
              Math.round((new Date().valueOf() - startDate.valueOf()) / 1000)
            ), //   convert elapsed count to seconds
            tap((elapsed) => {
              if (elapsed >= timeout) {
                this.timeout();
              }
            })
          )
        )
      );
    });
  }

  /**
   * Setup ping.
   *
   * Pings every ping-seconds only if is not timeout.
   * @param pingMillisec
   */
  protected setupPing(pingMillisec: number) {
    this.ping$ = interval(pingMillisec).pipe(filter(() => !this.isTimeout));
  }

  /**
   * Clear local store data used for cross tab comms
   */
  protected clearCrossBrowserStoreData() {
    localStorage.removeItem('idle-refresh');
    localStorage.removeItem('timeout');
  }

  /**
   * Setup local store listeners for cross tab comms
   */
  protected setupCrossTabListeners() {
    window.addEventListener('storage', (e) => {
      if (e.key === 'idle-refresh') {
        // call all events
        this.idleRefresh$.next();

        if (this.isInactivityTimer) {
          this.resetTimer();
        }
      }
      if (e.key === 'timeout') {
        this.timeout();
      }
    });
  }
}
